import { http } from '@/api/http';

/**
 * Care-pathway DTOs mirror `ai-hub/.../openapi.yaml` (PTV2-1173 contract).
 * Pinned by-hand until `pnpm generate:api` picks up the new spec; the field
 * names + nullability are kept in lock-step so the swap is mechanical.
 *
 * Three resource families share this slice:
 *   - per-patient runtime (`/persons/{guid}/care-pathways*`)
 *   - admin template catalogue (`/care-pathway-templates*`)
 *   - request bodies for the state / step mutations
 */

export type CarePathwayStatusCode = 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';
export type CarePathwayStepStatusCode =
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'SKIPPED';
export type CarePathwayTemplateStateCode = 'ACTIVE' | 'DISABLED';

export interface CarePathwayStepDto {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly status: CarePathwayStepStatusCode;
  readonly sort_order: number;
  readonly completion_note: string | null;
  readonly skip_rationale: string | null;
  readonly completed_at: string | null;
  readonly completed_by: string | null;
}

export interface CarePathwayDto {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly status: CarePathwayStatusCode;
  readonly template_id: string;
  readonly completion_reason: string | null;
  readonly enrolled_at: string | null;
  readonly enrolled_by: string | null;
  readonly closed_at: string | null;
  readonly closed_by: string | null;
  readonly steps: readonly CarePathwayStepDto[];
}

export interface CarePathwayTemplateStepDto {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly sort_order: number;
}

export interface CarePathwayTemplateDto {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly state: CarePathwayTemplateStateCode;
  readonly step_count: number;
  readonly enrollment_count: number;
  readonly steps: readonly CarePathwayTemplateStepDto[];
  readonly created_at: string | null;
  readonly modified_at: string | null;
}

/** Body for `PATCH /care-pathways/{guid}` — `reason` required for COMPLETED / CANCELLED. */
export interface CarePathwayStatusRequest {
  readonly status: CarePathwayStatusCode;
  readonly reason?: string;
}

/** Body for `PATCH /care-pathways/{guid}/steps/{guid}` — `skip_rationale` required for SKIPPED. */
export interface CarePathwayStepRequest {
  readonly status: CarePathwayStepStatusCode;
  readonly completion_note?: string;
  readonly skip_rationale?: string;
}

/** Body for `POST /care-pathway-templates` and `PATCH /care-pathway-templates/{guid}`. */
export interface CarePathwayTemplateInput {
  readonly name: string;
  readonly description: string;
  readonly steps: readonly {
    readonly id?: string;
    readonly name: string;
    readonly description?: string;
    readonly sort_order: number;
  }[];
}

export interface CarePathwayTemplateStateRequest {
  readonly state: CarePathwayTemplateStateCode;
}

export const carePathwaysApi = {
  /** GET /persons/{guid}/care-pathways — all enrollments (active + closed). */
  getCarePathways: (
    guid: string,
    options?: { signal?: AbortSignal },
  ): Promise<readonly CarePathwayDto[]> =>
    http.get<readonly CarePathwayDto[]>(
      `/persons/${encodeURIComponent(guid)}/care-pathways`,
      options,
    ),

  /** POST /persons/{guid}/care-pathways — enroll the patient in a template (server snapshots steps). */
  assignCarePathway: (
    guid: string,
    body: { template_id: string },
    options?: { signal?: AbortSignal },
  ): Promise<CarePathwayDto> =>
    http.post<CarePathwayDto>(
      `/persons/${encodeURIComponent(guid)}/care-pathways`,
      body,
      options,
    ),

  /** DELETE /persons/{guid}/care-pathways/{id} — hard-remove (enrolled-in-error). */
  removeCarePathway: (
    guid: string,
    id: string,
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.delete<undefined>(
      `/persons/${encodeURIComponent(guid)}/care-pathways/${encodeURIComponent(id)}`,
      options,
    ),

  /**
   * PATCH /persons/{guid}/care-pathways/{cpId} — change pathway state.
   * `reason` is required when transitioning to COMPLETED or CANCELLED;
   * the BE returns 422 on missing/blank reason for those transitions.
   */
  updateCarePathwayStatus: (
    guid: string,
    pathwayId: string,
    body: CarePathwayStatusRequest,
    options?: { signal?: AbortSignal },
  ): Promise<CarePathwayDto> =>
    http.patch<CarePathwayDto>(
      `/persons/${encodeURIComponent(guid)}/care-pathways/${encodeURIComponent(pathwayId)}`,
      body,
      options,
    ),

  /**
   * PATCH /persons/{guid}/care-pathways/{cpId}/steps/{stepId} — change step state.
   * `skip_rationale` required for SKIPPED (BE returns 422 without it).
   * `completion_note` is optional on COMPLETED.
   * Marking complete sets `completed_at` / `completed_by` server-side and
   * auto-advances the next NOT_STARTED step to IN_PROGRESS.
   */
  updateCarePathwayStep: (
    guid: string,
    pathwayId: string,
    stepId: string,
    body: CarePathwayStepRequest,
    options?: { signal?: AbortSignal },
  ): Promise<CarePathwayDto> =>
    http.patch<CarePathwayDto>(
      `/persons/${encodeURIComponent(guid)}/care-pathways/${encodeURIComponent(pathwayId)}/steps/${encodeURIComponent(stepId)}`,
      body,
      options,
    ),

  /** GET /care-pathway-templates — tenant catalog. `include_disabled=true` returns DISABLED rows too. */
  listCarePathwayTemplates: (
    params: { include_disabled?: boolean } = {},
    options?: { signal?: AbortSignal },
  ): Promise<readonly CarePathwayTemplateDto[]> =>
    http.get<readonly CarePathwayTemplateDto[]>(`/care-pathway-templates`, {
      ...options,
      query: params.include_disabled === true ? { include_disabled: true } : {},
    }),

  /** GET /care-pathway-templates/{id} — single template (admin edit). */
  getCarePathwayTemplate: (
    id: string,
    options?: { signal?: AbortSignal },
  ): Promise<CarePathwayTemplateDto> =>
    http.get<CarePathwayTemplateDto>(
      `/care-pathway-templates/${encodeURIComponent(id)}`,
      options,
    ),

  /** POST /care-pathway-templates — create template (admin). */
  createCarePathwayTemplate: (
    body: CarePathwayTemplateInput,
    options?: { signal?: AbortSignal },
  ): Promise<CarePathwayTemplateDto> =>
    http.post<CarePathwayTemplateDto>(`/care-pathway-templates`, body, options),

  /**
   * PATCH /care-pathway-templates/{id} — edit template (admin). Edits do NOT
   * propagate to existing enrollments (snapshot-on-enroll semantics).
   */
  updateCarePathwayTemplate: (
    id: string,
    body: CarePathwayTemplateInput,
    options?: { signal?: AbortSignal },
  ): Promise<CarePathwayTemplateDto> =>
    http.patch<CarePathwayTemplateDto>(
      `/care-pathway-templates/${encodeURIComponent(id)}`,
      body,
      options,
    ),

  /** PATCH /care-pathway-templates/{id}/state — disable / re-enable. */
  setCarePathwayTemplateState: (
    id: string,
    body: CarePathwayTemplateStateRequest,
    options?: { signal?: AbortSignal },
  ): Promise<CarePathwayTemplateDto> =>
    http.patch<CarePathwayTemplateDto>(
      `/care-pathway-templates/${encodeURIComponent(id)}/state`,
      body,
      options,
    ),

  /** DELETE /care-pathway-templates/{id} — hard-delete (409 if enrollments exist). */
  deleteCarePathwayTemplate: (
    id: string,
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.delete<undefined>(
      `/care-pathway-templates/${encodeURIComponent(id)}`,
      options,
    ),
};
